/**
 * 备份恢复（**N 域 N4 数据主权 · 「从备份恢复」**）—— 模式 / 裁决 / 预览与执行的**唯一契约源**
 *
 * 依据：N 域 PRD **§5.4.2**（恢复）· **§7.4 `AC-N4.16`–`AC-N4.22`**
 * · **§6.1 `N-D1`–`N-D3`**（不认识的新表保留 / 缺关键表警告 / 缺父表拒恢复）
 * · **§6.2 `N-O5`**（`overwrite` 未先导出 ⇒ 拦截）· **`N-O10`**（`merge` 必须报丢弃清单）
 * · 原型 `08-家庭与设置_原型.html` 的「态展示 · 恢复冲突裁决」区（**口径以原型为准**，铁律 14）·
 * **§6.4 铁律 3**（导出能力唯一在 C 域；本档是**恢复**，与它**对称但不共用实现**）。
 *
 * ## 🔴 三项计数的**口径**（本批首次定义 —— v1 只"数行数"，PRD 也没给定义）
 *
 * v1 的预览就是 `counts[k] = data[k].length`（**只数行数**，正是 PRD §1 批评的那点），
 * 所以"新增 / 覆盖 / 冲突"三项的确切含义必须在本批**定下来并写进代码**。取值依据 = 原型 `08`：
 * 「新增 1,284 笔 · 覆盖 96 笔 · ⚠️ 冲突 12 笔」，两条冲突样例分别是
 * 「本地「日用百货」· 备份「食品酒饮」—— 分类不一致」与「本地已软删除 · 备份中存在」。
 *
 * ⇒ **按主键逐行比对**（作用域 = 目标账本；主键从 `PRAGMA table_info` 现取，不手抄）：
 *
 * | 项 | 判据 | 用户看到的意思 |
 * |---|---|---|
 * | **新增 `added`** | 备份里有、本地**没有该主键** | 这些行会**插入** |
 * | **覆盖 `overwritten`** | 备份里有、本地**也有该主键** | 本地这 `n` 行会被备份值**改写**（含内容相同的幂等重写） |
 * | **冲突 `conflicted`** | `overwritten` 里**内容不一致**的那部分 | ⚠️ 这 `n` 行**需要你逐项裁决** |
 *
 * 🔴 **关系：`conflicted ⊆ overwritten`**（冲突是"覆盖里需要你看一眼的那部分"）——
 * 原型上冲突那个 tag 带 `--c-notice` 琥珀色、与前两个 tag 并列，正是"子集 + 需要关注"的呈现。
 * 集成测试断言这条关系（`conflicted <= overwritten`），**不允许**出现冲突比覆盖还多这种自相矛盾。
 *
 * ## 🔴 `overwrite` 的预览计数怎么读
 *
 * `overwrite` 会**先清空本账本的账本内容、再把备份整份写回** ⇒ 逐行比对对它没有意义。
 * ⇒ 预览一律按**上面那套（即 `merge` 的视角）**给计数（"若合会怎样"），
 * 额外用 {@link RestorePreviewView.overwriteImpact} 如实告知"覆盖模式会先清掉多少"。
 * 这样用户能在**同一屏**上比较两种模式，而不是读两组不同口径的数字。
 */

import { z } from 'zod';

// ─────────────────────────── 模式与裁决 ───────────────────────────

/** 恢复模式（`merge` 合并不清空 / `overwrite` 先清空再写回） */
export const RESTORE_MODES = ['merge', 'overwrite'] as const;
export type RestoreMode = (typeof RESTORE_MODES)[number];

/**
 * 冲突项的三选一裁决（**原型逐字**：`保留本地` / `采用备份` / `跳过`）。
 *
 * 🔴 三者的区别（`保留本地` 与 `跳过` **不是**同一件事，否则原型不会给三个按钮）：
 * | 裁决 | 本地行 | 备份行 |
 * |---|---|---|
 * | `keepLocal` | 不动 | **丢弃**（计入丢弃清单 —— `N-O10` 要的就是它） |
 * | `useBackup` | 用备份值改写 | 写入 |
 * | `skip` | 不动 | **不计入丢弃清单**（"这一项我还没决定"，下次预览仍会出现） |
 */
export const RESTORE_RESOLUTIONS = ['keepLocal', 'useBackup', 'skip'] as const;
export type RestoreResolution = (typeof RESTORE_RESOLUTIONS)[number];

// ─────────────────────────── 预览 ───────────────────────────

/** 三项计数（口径见文件头） */
export interface RestoreCounts {
  readonly added: number;
  readonly overwritten: number;
  readonly conflicted: number;
}

/**
 * 一条冲突项 —— 🔴 **只下发结构化事实，中文文案在端侧**（与服务端出数字、端侧出文案的既有分工一致）。
 *
 * `changedColumns` + 两侧的**变化列原值**是"分类不一致 / 已软删除"这类描述的**原料**：
 * 端侧可以列出"列 `category_id`：本地 `a` · 备份 `b`"。⚠️ 刻意**不给整行** ——
 * 一行可能有几十列，整行下发会把预览响应撑到与备份同量级。
 */
export interface RestoreConflictView {
  /** 表名 */
  readonly table: string;
  /** 主键的规范化字符串（单列 `id=x`；复合主键按 `pk` 序号拼 `a=1|b=2`） */
  readonly key: string;
  /** 内容不一致的列名（升序 —— 可断言） */
  readonly changedColumns: readonly string[];
  /** 变化列的**本地**值 */
  readonly localValues: Readonly<Record<string, string | number | null>>;
  /** 变化列的**备份**值 */
  readonly backupValues: Readonly<Record<string, string | number | null>>;
}

/** 一条**丢弃**（`merge` 下因裁决而**未写入**的备份行 —— `N-O10` 的"丢弃清单"） */
export interface RestoreDiscardView {
  readonly table: string;
  readonly key: string;
  /** 为什么被丢：`keptLocal` = 用户裁决保留本地 / `skipped` = 该表不在可写白名单 */
  readonly reason: 'keptLocal' | 'tableNotWritable';
}

/**
 * 预览警告（`N-D1` / `N-D2` / 未知列）。
 *
 * 🔴 `code` 是**判据**，`message` 是**事实描述**（端侧据此决定要不要"二次确认"）——
 * 端侧**不得**按 `message` 文本做判断（`message` 会改文案）。
 */
export interface RestoreWarning {
  readonly code: 'newerVersion' | 'missingCriticalTables' | 'unknownColumns' | 'unknownTables';
  /** 涉及的表名（升序） */
  readonly tables: readonly string[];
  /** 给人看的事实描述 */
  readonly message: string;
}

/** 一张**备份里有、但恢复不写回**的表 + 原因（🔴 必须逐条如实告知，不许静默跳过） */
export interface RestoreNotWritableView {
  readonly name: string;
  readonly reason: string;
}

/** `POST /api/settings/restore/preview` 请求体 */
export interface RestorePreviewRequest {
  /** 备份文件的**已解析内容**（端侧读 `.json` 后原样上传 —— 服务端负责形状判定与如实报错） */
  readonly backup: unknown;
}

/** 恢复预览 */
export interface RestorePreviewView {
  /** 备份里的账本名（端侧二次输入要比对它） */
  readonly backupLedgerName: string;
  readonly backupLedgerId: string;
  readonly backupExportedAt: number;
  readonly backupSchemaVersion: number;
  readonly localSchemaVersion: number;
  readonly counts: RestoreCounts;
  /** 冲突明细（**截断**——`conflictsTruncated` 为 `true` 时端侧必须显示"另有 N 笔"） */
  readonly conflicts: readonly RestoreConflictView[];
  readonly conflictsTruncated: boolean;
  /** `overwrite` 会先清掉多少（`merge` 下同样是**事实**，供用户比较两种模式） */
  readonly overwriteImpact: {
    /** 会被清空的表数（只数**可写**的表） */
    readonly tables: number;
    /** 会被清掉的本账本行数 */
    readonly rows: number;
  };
  readonly warnings: readonly RestoreWarning[];
  readonly notWritable: readonly RestoreNotWritableView[];
  /** 备份里有、但**看不到行**的表数（空表也算 —— 它决定了 `N-D2`） */
  readonly missingCriticalTables: readonly string[];
  /**
   * 🔴 执行前**必须**由端侧满足的前置条件（服务端逐条校验，缺一即 400）。
   * 端侧**不得**自己判断"要不要二次确认"，一律读这里。
   */
  readonly requires: {
    /** `overwrite` ⇒ 必须先导出（`N-O5`） */
    readonly preExport: boolean;
    /** `overwrite` ⇒ 必须二次输入账本名（`AC-N4.20`） */
    readonly confirmLedgerName: boolean;
    /** 缺关键表 ⇒ 必须显式确认（`N-D2`） */
    readonly missingTablesAck: boolean;
  };
}

// ─────────────────────────── 执行 ───────────────────────────

/** 一条裁决（逐项） */
export interface RestoreDecision {
  readonly table: string;
  readonly key: string;
  readonly resolution: RestoreResolution;
}

/** `POST /api/settings/restore` 请求体 */
export interface RestoreRequest {
  readonly backup: unknown;
  readonly mode: RestoreMode;
  /** 逐项裁决；**未给出的冲突项按 `skip` 处理**（见下） */
  readonly decisions?: readonly RestoreDecision[];
  /** `overwrite` 必填：二次输入账本名（`AC-N4.20`） */
  readonly confirmLedgerName?: string;
  /**
   * `overwrite` 必填：**已导出**的显式确认（`AC-N4.19` / `N-O5`）。
   *
   * ⚠️ **诚实说明**：服务端**无法验证**"用户真的点了导出"（那需要记录导出历史 —— 属新载体）。
   * 所以这是一个**显式确认旗标**而非证据：端侧的真实流程是"先下载备份 → 才能勾选它"。
   * 与 `AC-N4.21`（覆盖前自动快照）同一处待 DR，见 changelog 的诚实登记。
   */
  readonly exportedBeforehand?: boolean;
  /** 缺关键表时的显式确认（`N-D2`） */
  readonly acknowledgeMissingTables?: boolean;
}

/** 执行结果 */
export interface RestoreResultView {
  readonly mode: RestoreMode;
  /** 实际写入（`inserted` = 新插入 / `updated` = 改写既有行） */
  readonly written: {
    readonly inserted: number;
    readonly updated: number;
  };
  /** 被丢弃的备份行（**截断** —— `discardsTruncated` 必须上报，静默截断 = 让用户以为就这么多） */
  readonly discards: readonly RestoreDiscardView[];
  readonly discardsTruncated: boolean;
  /**
   * 🔴 **完整性核验**（`AC-N4.22` 的运行期半边 —— 真恒等式在集成测试里）。
   *
   * 三项都应为 `0`；非 `0` ⇒ 如实上报（**不**伪装成功）：
   * - `legsWithoutAccount`：有腿指向本账本不存在的账户（该腿**永远不进余额** = 静默丢数据）；
   * - `transactionsWithoutLegs`：一笔交易一条腿都没有（父行没了内容）；
   * - `crossLedgerLegs`：腿与所属交易的 `ledger_id` 不一致。
   */
  readonly integrity: {
    readonly legsWithoutAccount: number;
    readonly transactionsWithoutLegs: number;
    readonly crossLedgerLegs: number;
  };
  /** 执行后**逐账户余额**（`{ accountId: 分 }` —— 端侧与"导出时的余额"对不上就是问题） */
  readonly accountBalances: Readonly<Record<string, number>>;
}

// ─────────────────────────── 唯一命名（端侧直接用，禁各写一份） ───────────────────────────

/** 三项计数的中文名（🔴 唯一源 —— 服务端不产文案，端侧从这里取） */
export const RESTORE_COUNT_LABELS: Readonly<Record<keyof RestoreCounts, string>> = {
  added: '新增',
  overwritten: '覆盖',
  conflicted: '冲突',
};

/** 三个裁决的中文名（原型逐字） */
export const RESTORE_RESOLUTION_LABELS: Readonly<Record<RestoreResolution, string>> = {
  keepLocal: '保留本地',
  useBackup: '采用备份',
  skip: '跳过',
};

/** 两种模式的中文名（原型逐字） */
export const RESTORE_MODE_LABELS: Readonly<Record<RestoreMode, string>> = {
  merge: '合并模式',
  overwrite: '覆盖模式',
};

/**
 * 两种模式的**一句话说明**（端侧渲染，禁各写一份）。
 *
 * 🔴 `overwrite` 的说明里**必须**含"先清空"这件事（`overwriteImpact` 讲的就是它）——
 * 用户看到"覆盖 96 笔"会以为只改 96 行，而实际是把本账本清空后整份写回。
 * 前一句取自原型「合并模式（会列出丢弃清单）」；后一句按 `overwriteImpact` 的语义补足。
 */
export const RESTORE_MODE_NOTES: Readonly<Record<RestoreMode, string>> = {
  merge: '保留本地已有数据，只把备份里多出来的写进来；被裁决为「保留本地」的备份行会进丢弃清单。',
  overwrite: '🔴 先清空本账本的账本内容，再把备份整份写回（不可逆）。',
};

/** 预览警告 `code` 的中文名（🔴 唯一源；端侧**不得**按 `message` 文本判断） */
export const RESTORE_WARNING_LABELS: Readonly<Record<RestoreWarning['code'], string>> = {
  newerVersion: '备份来自更新的版本',
  missingCriticalTables: '备份缺少关键表',
  unknownColumns: '备份含本版本不认识的列',
  unknownTables: '备份含本版本不认识的表',
};

/** 丢弃原因的中文名（`N-O10` 的丢弃清单逐条要说明"为什么没写"） */
export const RESTORE_DISCARD_REASON_LABELS: Readonly<Record<RestoreDiscardView['reason'], string>> = {
  keptLocal: '你裁决为「保留本地」',
  tableNotWritable: '该表不在写回范围内',
};

// ─────────────────────────── Zod（架构 §6.1：Zod 唯一契约源） ───────────────────────────

/**
 * ⚠️ `backup` 用 `z.unknown()`：备份文件的形状**由 `contract/backup.ts` 的 `toBackupJson` 定义**，
 * 在这里再写一遍 `z.object({...})` = 第二套形状规则（`DR-021` 禁令① 的同型问题）。
 * 服务端解析时按 `format` 分派并**如实报错**（不猜、不静默）。
 */
export const RestorePreviewRequestSchema = z.object({
  backup: z.unknown(),
});

const RestoreDecisionSchema = z.object({
  table: z.string().min(1).max(64),
  key: z.string().min(1).max(512),
  resolution: z.enum(RESTORE_RESOLUTIONS),
});

export const RestoreRequestSchema = z.object({
  backup: z.unknown(),
  mode: z.enum(RESTORE_MODES),
  decisions: z.array(RestoreDecisionSchema).max(10_000).optional(),
  confirmLedgerName: z.string().max(64).optional(),
  exportedBeforehand: z.boolean().optional(),
  acknowledgeMissingTables: z.boolean().optional(),
});

/** 执行结果里 `accountBalances` 的键是账户 id —— 键名不参与契约演化（只做形状说明） */
export type AccountBalanceMap = RestoreResultView['accountBalances'];
